// All bot copy, triggers and limits in one place. Keep it free of long dashes.

export const BOT_NAME = 'Routy';
export const MASCOT_THEME = 'teal'; // teal | indigo | sunset (see Mascot.jsx)

export const LIMITS = {
    maxNudgesPerSession: 2,   // soft nudges only; booking pain nudges (payment failed etc.) bypass this
    nudgeDelayMs: 1200,       // pause after a trigger fires, so it doesn't feel jumpy
    bubbleAutoHideMs: 15000,
    snoozeHours: 24,
    homeIdleMs: 20000,
    homeScrollPx: 600,
};

// Highest priority first
export const SEARCH_TRIGGERS = ['noRoutes', 'allWaitlisted', 'tatkalWindow', 'returnedAfterAway', 'dateFlips', 'deepScrollNoBook', 'exitIntent'];
export const HOME_TRIGGERS = ['exitIntent', 'homeScroll', 'homeIdle'];

// Triggers where the user is clearly stuck. "No thanks" on these offers a seat alert instead.
export const PAIN_TRIGGERS = ['noRoutes', 'allWaitlisted', 'tatkalWindow'];

// ── Booking pages (/book-flight, /book-bus, /book-bus/status) ─────────────
export const BOOKING_TRIGGERS = ['bookingFailed', 'paymentFailed', 'bookError', 'paymentDismissed', 'formIdle', 'bookReturned', 'bookExitIntent'];

// Money or a booking is at stake: these ignore the per-session nudge cap (still once per booking)
export const BOOKING_PAIN_TRIGGERS = ['bookingFailed', 'paymentFailed', 'bookError', 'paymentDismissed'];

// Accepting these nudges goes straight to the phone step with the reason already known
export const BOOKING_TRIGGER_REASON = {
    bookingFailed: 'booking_failed',
    paymentFailed: 'payment_issue',
    bookError: 'tech_issue',
    formIdle: 'details_help',
};

// ── Curated trip pages (/trips/:slug) ─────────────────────────────────────
export const TRIP_TRIGGERS = ['tripCheckoutClosed', 'tripAgentStuck', 'tripReturned', 'exitIntent', 'tripIdle'];

// Closer to buying: these ignore the per-session nudge cap (still once per trip)
export const TRIP_PAIN_TRIGGERS = ['tripCheckoutClosed', 'tripAgentStuck'];

// Stored as trigger_type on the lead row. Must match BOT_TRIGGER_TYPES in contact_lead_handler.py
export const BOT_TRIGGER_TYPE = {
    noRoutes: 'bot_no_routes',
    allWaitlisted: 'bot_all_waitlisted',
    tatkalWindow: 'bot_tatkal_window',
    returnedAfterAway: 'bot_returned_after_away',
    dateFlips: 'bot_date_flips',
    deepScrollNoBook: 'bot_deep_scroll',
    exitIntent: 'bot_exit_intent',
    homeScroll: 'bot_home_scroll',
    homeIdle: 'bot_home_idle',
    // booking pages
    bookingFailed: 'bot_book_failed',
    paymentFailed: 'bot_book_payment_failed',
    bookError: 'bot_book_error',
    paymentDismissed: 'bot_book_payment_closed',
    formIdle: 'bot_book_form_idle',
    bookReturned: 'bot_book_returned',
    bookExitIntent: 'bot_book_exit_intent',
    bookLeave: 'bot_book_leave',     // pressed back / close on a booking page
    // curated trip pages
    tripCheckoutClosed: 'bot_trip_checkout_closed',
    tripAgentStuck: 'bot_trip_agent_stuck',
    tripReturned: 'bot_trip_returned',
    tripIdle: 'bot_trip_idle',
    tripReserve: 'bot_trip_reserve',          // "Reserve this trip" in the booking summary
    tripChatHuman: 'bot_trip_chat_human',     // "talk to a person" from the AI trip editor
    manual: 'bot_manual',            // user tapped the mascot without a nudge
};

export const shortPlace = (label) =>
    String(label || '')
        .replace(/‎/g, '')
        .split(' (')[0]
        .replace(/\s*(railway station|airport)\s*$/i, '')
        .trim();

const dest = (s) => shortPlace(s?.dest_name) || 'your destination';
const modeWord = (b) => (b?.mode === 'bus' ? 'bus' : 'flight');
export const bookingRoute = (b) => (b?.src_name && b?.dest_name
    ? `${shortPlace(b.src_name)} → ${shortPlace(b.dest_name)}`
    : '');

export const NUDGES = {
    noRoutes: (s) => `Couldn't find a good route to ${dest(s)}. Want our travel team to look for options?`,
    allWaitlisted: (s) => `Direct trains to ${dest(s)} look full 😕 Want our team to try for a confirmed seat?`,
    tatkalWindow: () => 'Travelling tomorrow? Tatkal seats go in minutes. Want our team to try for you?',
    returnedAfterAway: () => "Didn't work out? Our travel team can help you book this trip.",
    dateFlips: () => 'Dates flexible? Our team can find you the best day to travel.',
    deepScrollNoBook: () => 'Not finding the right one? A real person can look for you.',
    exitIntent: () => 'Before you go: want our team to find you a confirmed seat?',
    homeScroll: () => 'Festive rush is on. Need confirmed train or flight tickets? I can help.',
    homeIdle: () => 'Hi! Stuck on a waitlist or hunting for flights? I can help.',
};

export const BOOKING_NUDGES = {
    bookingFailed: (b) => `Sorry, your ${modeWord(b)} booking hit a snag 😕 Want our team to check it and call you?`,
    paymentFailed: () => "Payment didn't go through. If money was deducted, it comes back automatically. Want our team to help you finish booking?",
    bookError: () => "Looks like something didn't load right 😕 Want our team to complete this booking for you?",
    paymentDismissed: () => "Payment didn't finish? Tell me what stopped you and I'll help.",
    formIdle: () => 'Need a hand with the details? Our team can book this for you.',
    bookReturned: (b) => `Welcome back! Still booking${bookingRoute(b) ? ` ${bookingRoute(b)}` : ' this trip'}? Anything I can help with?`,
    bookExitIntent: () => 'Leaving already? Tell me what stopped you. Takes 2 seconds.',
};

const tripName = (t) => t?.title || 'this trip';

export const TRIP_NUDGES = {
    tripCheckoutClosed: () => 'Not ready to book yet? Our trip expert can answer questions or hold this plan for you.',
    tripAgentStuck: () => "I'm not getting that right. Want a trip expert to make the change for you?",
    tripReturned: (t) => `Welcome back! Still thinking about ${tripName(t)}? I can get you a quick callback.`,
    exitIntent: () => 'Before you go: want us to save this plan and send it to you on WhatsApp?',
    tripIdle: () => 'Any questions about this trip? Ask me and a trip expert will reply.',
};

export const TRIP_PRIMARY = (trigger) => (trigger === 'exitIntent' ? 'Send it to me' : 'Yes, please');

export const NUDGE_PRIMARY = (trigger) =>
    (HOME_TRIGGERS.includes(trigger) ? "Let's go" : 'Yes, help me');

export const BOOKING_PRIMARY = (trigger) =>
    (['paymentDismissed', 'bookExitIntent'].includes(trigger) ? 'Tell you why' : 'Yes, help me');

export const MOOD_FOR_TRIGGER = (trigger) =>
    (['noRoutes', 'allWaitlisted', 'bookingFailed', 'paymentFailed', 'bookError'].includes(trigger) ? 'sad'
        : trigger === 'tripAgentStuck' ? 'think' : 'wave');

// Why people drop off a booking. value is stored as drop_reason (DROP_REASONS in contact_lead_handler.py).
// offer: what the bot does next. call = offer a callback, plan = offer the trip planner, none = say bye.
export const BOOKING_REASONS = [
    { value: 'payment_issue', label: 'Payment problem', offer: 'call', reply: 'Sorry about that. If any money was deducted, it is refunded automatically.' },
    { value: 'price_high', label: 'Price too high', offer: 'call', reply: 'Fair enough. Our team sometimes finds cheaper options or better dates.' },
    { value: 'tech_issue', label: "Something's not working", offer: 'call', reply: 'Sorry! Our team can complete this booking for you.' },
    { value: 'availability', label: 'Seat or fare changed', offer: 'call', reply: 'That happens when seats sell fast. Our team can find the next best option.' },
    { value: 'details_help', label: 'Unsure about the details', offer: 'call', reply: 'Happy to help with that.' },
    { value: 'change_plans', label: 'My plans changed', offer: 'plan', reply: 'No problem. Want to look at other options in the trip planner?' },
    { value: 'just_checking', label: 'Just checking prices', offer: 'none', reply: 'Got it. Fares change often, so book when you are ready 👍' },
    { value: 'other', label: 'Something else', offer: 'call', reply: 'Thanks for telling me.' },
];

// Reasons not shown as chips but set by a trigger
export const REASON_LABEL = {
    booking_failed: 'Ticket could not be confirmed',
};

// Chips on a manual open of the bot on a booking page (no "just checking" there)
export const BOOKING_MENU_REASONS = ['payment_issue', 'tech_issue', 'availability', 'price_high', 'details_help', 'other'];

export const COPY = {
    homeGreeting: `Hi, I'm ${BOT_NAME} 👋 Planning a trip, or stuck on a waitlist?`,
    homeMenuAfterNudge: "Pick one and I'll take it from there.",
    searchGreeting: (s) => `Hi, I'm ${BOT_NAME} 👋 Need help with ${s ? `${shortPlace(s.src_name)} → ${dest(s)}` : 'this trip'}?`,
    alreadyHave: "We've got your request ✓ Our travel team will reach out soon.",
    askRoute: 'Where are you travelling from and to?',
    confirmTrip: (summary) => `Got it: ${summary}.`,
    askFlex: 'Are your dates flexible?',
    askModes: 'How would you like to travel? Pick all that work for you.',
    askPhoneCallback: (callLine) => `What's your mobile number? ${callLine}`,
    askPhoneAlert: "Share your number and we'll WhatsApp you as soon as a confirmed seat opens up.",
    phoneNote: "We'll send a confirmation on WhatsApp too.",
    alertOffer: 'No worries. Want a WhatsApp alert if a confirmed seat opens up?',
    bye: "No problem. I'm right here if you need me.",
    handoffChat: "Opening the trip planner for you. It'll find routes in seconds.",
    doneCallback: (phone, when) => `Done! 🎉 Our travel team will call you on ${phone} ${when}.`,
    doneAlert: (phone) => `Done! 🎉 We'll WhatsApp you on ${phone} the moment a confirmed seat opens up.`,
    submitError: "Hmm, that didn't go through. Please try again.",

    // booking pages
    bookingGreeting: (b) => `Hi, I'm ${BOT_NAME} 👋 Need help with your ${modeWord(b)} booking${bookingRoute(b) ? ` for ${bookingRoute(b)}` : ''}?`,
    bookingMenuAsk: "What's going on? Pick one.",
    exitAsk: 'Before you go, what stopped you from booking?',
    askOther: 'Tell me in a few words what went wrong.',
    offerCall: 'Want our travel team to call you and help finish this booking?',
    byeBooking: 'No problem. Your booking is right here whenever you are ready.',
    stay: "Great, take your time. I'm right here if you need me.",
    alreadyBooking: "We've got your request for this booking ✓ Our travel team will reach out soon.",
    // curated trip pages
    tripGreeting: (t) => `Hi, I'm ${BOT_NAME} 👋 Want help with ${tripName(t)}?`,
    tripMenuAsk: 'What would you like to do?',
    tripReserveIntro: (t) => `Great choice! I'll pass your version of ${tripName(t)}${t?.total ? ` (Rs ${Number(t.total).toLocaleString('en-IN')} for ${t.pax})` : ''} to our trip team. They confirm every booking, then WhatsApp you a payment link.`,
    askTripQuery: "What's your question? Our trip expert will reply.",
    askTripCustom: 'What would you like changed? Tell me in a line.',
    tripQueryAck: 'Got it. Where should our expert reach you?',
    tripSelfEdit: 'Tap "Edit" or use the AI box on the page. Tell it things like "cheaper hotel" or "add a day". I am here if you need a person.',
    tripSendPlan: "I'll WhatsApp you this exact plan with prices. What's your number?",
    alreadyTrip: "We've got your request for this trip ✓ Our trip expert will reach out soon.",
    doneTrip: (phone, when, intent) => (intent === 'trip_booking'
        ? `Done! 🎉 Our trip team will call you on ${phone} ${when} to confirm the bookings.`
        : intent === 'trip_plan'
            ? `Done! 🎉 We'll WhatsApp the plan to ${phone} shortly.`
            : `Done! 🎉 Our trip expert will get back to you on ${phone} ${when}.`),
    doneBooking: (phone, when, ref) => `Done! 🎉 Our travel team will call you on ${phone} ${when}.${ref ? ` Your booking ref is ${ref}.` : ''}`,
};

export const FLEX_OPTIONS = [
    { value: 'exact', label: 'Exact date', days: 0 },
    { value: 'pm1', label: '±1 day', days: 1 },
    { value: 'pm3', label: '±2-3 days', days: 3 },
];

// Multi-select in the chat. Order here is the order modes are listed in the lead.
export const MODE_OPTIONS = ['Train', 'Flight', 'Bus'];

// Label on the extended FAB (the "Hi, I'm Routy" pill)
export const FAB_COPY = ({ page, leadSubmitted, pain }) => {
    const title = `Hi, I'm ${BOT_NAME} 👋`;
    if (leadSubmitted) return { title, sub: 'Request received ✓' };
    if (page === 'search') return { title, sub: pain ? 'Seats full? I can help' : 'Can I help with this trip?' };
    if (page === 'booking') return { title, sub: pain ? 'Stuck? I can help' : 'Need help booking?' };
    if (page === 'trip') return { title, sub: pain ? 'Want an expert to book it?' : 'Questions about this trip?' };
    return { title, sub: 'Need confirmed tickets?' };
};
